// Uyarılar — kural tipi/temsilci/marka/tarih/durum filtreleri + toplu onay (ack).
//
// ONAY = AÇIKLAMA ZORUNLU (sahibin kuralı, 2026-09-13). "Onayla" ve "Seçilenleri Onayla"
// doğrudan istek ATMAZ: ikisi de önce açıklama modalını açar, sunucuya yalnız dolu bir
// açıklamayla gidilir (boşu sunucu da 400 `note_required` ile reddeder — tek kapı UI
// değil). Toplu onayda TEK açıklama seçimin tamamına yazılır.

import { useEffect, useState } from 'react';
import { ACK_NOTE_MAX, ackAlert, ackAlerts, getAlertRuleTitles, getAlerts } from '../api';
import {
  DARK_ALERT_RULES, RETIRED_ALERT_RULES, hasDarkBrand, RULE_TITLES, SEV_META, brandLabel, tzOfBrands,
} from '../constants';
import { fmtDate, fmtRel, fmtStamp } from '../format';
import { useExpanded, useRoster } from '../hooks';
import type { Alert, AlertRuleTitles, AlertRuleType, AlertStatus, Brand, BrandId } from '../types';
import { EmptyState, Icon, Modal, Select, Skeleton, ghostBtnStyle, inputStyle, primaryBtnStyle, smallCheckStyle } from '../ui';

// Uyarı damgaları UYARININ MARKASININ dilimindedir (BetBooker=Akra), tarayıcınınki değil:
// bir uyarı temsilcinin vardiyasıyla okunur ("10:12'de gelmedi"), süpervizörün saatiyle
// değil. Eskiden `toLocaleString('tr-TR')` dilimsizdi ⇒ BetBooker uyarıları 3 saat ileri
// görünüyordu. Sunucu eşi: src/engine/istanbul.js hhmmInTz. Bkz. format.ts fmtStamp.

const alDateInputStyle = {
  background: 'var(--inset)',
  border: '1px solid var(--border)',
  borderRadius: '9px',
  padding: '8px 10px',
  color: 'var(--text)',
  fontSize: '12px',
  outline: 'none',
  colorScheme: 'dark',
} as const;

export default function Alerts({
  brands,
  availableBrands,
  onAlertsChanged,
}: {
  brands: BrandId[];
  availableBrands: Brand[];
  onAlertsChanged: () => void;
}) {
  const roster = useRoster();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  // Kural tipi başlıkları CANLI eşiklerle sunucudan gelir: `uzun_bosluk` başlığı
  // thresholds.longGapMin'i alıntılar (Ayarlar'dan 30 → 15 yapılınca satırlar
  // "Uzun boşluk (>15 dk)" der), açılır liste de aynı metni yazmalı. İstek dönene
  // kadar — ya da başarısız olursa — RULE_TITLES'ın varsayılan-eşikli metni durur.
  const [ruleTitles, setRuleTitles] = useState<AlertRuleTitles>(RULE_TITLES);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [alType, setAlType] = useState<AlertRuleType | 'all'>('all');
  const [alAgent, setAlAgent] = useState<string>('all');
  const [alBrand, setAlBrand] = useState<BrandId | 'all'>('all');
  const [alStart, setAlStart] = useState('');
  const [alEnd, setAlEnd] = useState('');
  const [alStatus, setAlStatus] = useState<AlertStatus | 'all'>('all');
  const { expanded, toggle } = useExpanded();
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [acking, setAcking] = useState(false);
  const [tick, setTick] = useState(0);

  const brandsKey = brands.join(',');

  // Kapsamda DARK markası (Vegas/Prime) yoksa DARK kuralları filtrede sunulmaz;
  // seçili kalmış eski bir DARK kuralı 'all' gibi davranır (sunucu da göstermez).
  const darkVisible = hasDarkBrand(brands.length ? brands : availableBrands.map((b) => b.id));
  const alTypeEff = !darkVisible && DARK_ALERT_RULES.includes(alType as AlertRuleType) ? 'all' : alType;

  useEffect(() => {
    let alive = true;
    setAlertsLoading(true);
    getAlerts({
      brands,
      ruleType: alTypeEff,
      agentId: alAgent,
      brand: alBrand,
      start: alStart,
      end: alEnd,
      status: alStatus,
    }).then((rows) => {
      if (!alive) return;
      setAlerts(rows);
      setAlertsLoading(false);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandsKey, alTypeEff, alAgent, alBrand, alStart, alEnd, alStatus, tick]);

  // Eşikler oturum içinde nadiren değişir; sayfa montajında bir kez okunur.
  useEffect(() => {
    let alive = true;
    getAlertRuleTitles()
      .then((t) => { if (alive) setRuleTitles(t); })
      .catch(() => { /* varsayılan başlıklar kalır — filtre çalışmaya devam eder */ });
    return () => {
      alive = false;
    };
  }, []);

  const reload = () => setTick((t) => t + 1);

  const resetAlFilters = () => {
    setAlType('all');
    setAlAgent('all');
    setAlBrand('all');
    setAlStart('');
    setAlEnd('');
    setAlStatus('all');
  };

  const toggleAlSelect = (id: string) =>
    setSelected((st) => {
      const sel = { ...st };
      if (sel[id]) delete sel[id];
      else sel[id] = true;
      return sel;
    });

  // Tümünü seç: hepsi zaten seçiliyse temizler, değilse hepsini seçer.
  const toggleAlSelectAll = (ids: string[]) =>
    setSelected((st) => {
      const all = ids.length > 0 && ids.every((id) => st[id]);
      const sel = { ...st };
      ids.forEach((id) => {
        if (all) delete sel[id];
        else sel[id] = true;
      });
      return sel;
    });

  // Açıklama modalı: hangi id'ler onaylanacak (tekil = 1 eleman). null = kapalı. Id listesi
  // modal AÇILIRKEN dondurulur — kullanıcı yazarken liste yenilense de onaylanan küme
  // gördüğü kümedir.
  const [ackPending, setAckPending] = useState<string[] | null>(null);
  const [ackNote, setAckNote] = useState('');
  const [ackErr, setAckErr] = useState('');

  const openAckOne = (id: string) => {
    setAckNote('');
    setAckErr('');
    setAckPending([id]);
  };

  const openAckSelected = () => {
    if (acking) return;
    // Yalnızca ŞU AN görünen ve hâlâ 'new' olan uyarılar onaylanır. `selected` filtre/marka/tarih
    // değiştiğinde temizlenmediğinden, gizlenmiş eski seçimler de Object.keys(selected) içinde
    // kalıyordu: buton "3 seçili" derken denetçinin hiç görmediği onlarca uyarı onaylanabiliyordu.
    const visibleNew = new Set(alerts.filter((a) => a.status === 'new').map((a) => a.id));
    const ids = Object.keys(selected).filter((id) => visibleNew.has(id));
    if (!ids.length) return;
    setAckNote('');
    setAckErr('');
    setAckPending(ids);
  };

  const closeAck = () => {
    if (acking) return;
    setAckPending(null);
  };

  const confirmAck = async () => {
    if (acking || !ackPending) return;
    const note = ackNote.trim();
    if (!note) return; // buton zaten kapalı; klavye yolu için ikinci kapı
    setAcking(true);
    setAckErr('');
    try {
      if (ackPending.length === 1) {
        await ackAlert(ackPending[0], note);
        const id = ackPending[0];
        setSelected((st) => {
          const sel = { ...st };
          delete sel[id];
          return sel;
        });
      } else {
        await ackAlerts(ackPending, note); // 500'lük parçalara bölünür (api.ts) — "tümünü seç" 1000'e kadar çıkabilir
        setSelected({});
      }
      setAckPending(null);
    } catch {
      // Modal AÇIK kalır, yazılan açıklama kaybolmaz — yeniden denenebilir.
      setAckErr('Onay kaydedilemedi. Tekrar deneyin.');
    } finally {
      // Hata olsa bile buton "Onaylanıyor…" durumunda takılı kalmasın.
      setAcking(false);
    }
    reload();
    onAlertsChanged();
  };

  const ackNoteOk = ackNote.trim().length > 0 && ackNote.length <= ACK_NOTE_MAX;
  const canConfirmAck = ackNoteOk && !acking;

  const nowA = Date.now();
  const newVisibleIds = alerts.filter((a) => a.status === 'new').map((a) => a.id);
  const alSelectedCount = newVisibleIds.filter((id) => selected[id]).length;
  const alAllSelected = newVisibleIds.length > 0 && newVisibleIds.every((id) => selected[id]);

  // Anahtar kümesi ve SIRA yerel katalogdan gelir (sunucu beklenmedik bir kural tipi
  // yollarsa filtreye seçenek olarak sızmasın); yalnızca ETİKET canlı başlıktan.
  const ruleTypeOptions = [{ value: 'all', label: 'Tüm kural tipleri' }].concat(
    (Object.keys(RULE_TITLES) as AlertRuleType[])
      .filter((k) => darkVisible || !DARK_ALERT_RULES.includes(k))
      .filter((k) => !RETIRED_ALERT_RULES.includes(k))
      .map((k) => ({ value: k, label: ruleTitles[k] || RULE_TITLES[k] })),
  );
  const agentOptions = [{ value: 'all', label: 'Tüm temsilciler' }].concat(
    roster.filter((a) => a.brands.some((b) => brands.includes(b))).map((a) => ({ value: a.id, label: a.name })),
  );
  const alBrandOptions = [{ value: 'all', label: 'Tüm markalar' }].concat(
    availableBrands.filter((b) => brands.includes(b.id)).map((b) => ({ value: b.id, label: b.name })),
  );
  const statusOptions = [
    { value: 'all', label: 'Tüm durumlar' },
    { value: 'new', label: 'Yeni' },
    { value: 'ack', label: 'Onaylandı' },
  ];

  const alLoading = alertsLoading && alerts.length === 0;
  const alShowEmpty = !alLoading && alerts.length === 0;
  const alShowData = alerts.length > 0;
  const alEmptyReason =
    brands.length === 0 ? 'Üstteki marka filtresinden en az bir marka seçin.' : 'Seçili filtrelerle eşleşen uyarı yok.';
  const ackSelBtnLabel = acking
    ? 'Onaylanıyor…'
    : alSelectedCount
      ? 'Seçilenleri Onayla (' + alSelectedCount + ')'
      : 'Seçilenleri Onayla';

  return (
    <>
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: '14px',
          padding: '14px 16px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap',
        }}
      >
        <Select compact value={alTypeEff} onChange={(v) => setAlType(v as AlertRuleType | 'all')} options={ruleTypeOptions} />
        <Select compact value={alAgent} onChange={(v) => setAlAgent(v)} options={agentOptions} />
        <Select compact value={alBrand} onChange={(v) => setAlBrand(v as BrandId | 'all')} options={alBrandOptions} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <input type="date" value={alStart} onChange={(e) => setAlStart(e.target.value)} style={alDateInputStyle} />
          <span style={{ color: 'var(--faint)' }}>–</span>
          <input type="date" value={alEnd} onChange={(e) => setAlEnd(e.target.value)} style={alDateInputStyle} />
        </div>
        <Select compact value={alStatus} onChange={(v) => setAlStatus(v as AlertStatus | 'all')} options={statusOptions} />
        <button
          onClick={resetAlFilters}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '9px 12px',
            background: 'transparent',
            border: '1px solid var(--border)',
            borderRadius: '9px',
            color: 'var(--dim)',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <Icon name="restart_alt" size={16} />
          Temizle
        </button>
      </div>

      {alLoading && <Skeleton heights={[70, 70, 70]} radius={10} gap={10} />}

      {alShowEmpty && <EmptyState icon="task_alt" iconColor="#37c46b" title="Uyarı yok" reason={alEmptyReason} />}

      {alShowData && (
        <>
          {newVisibleIds.length > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '11px',
                padding: '10px 14px',
                marginBottom: '14px',
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: '10px',
              }}
            >
              <div onClick={() => toggleAlSelectAll(newVisibleIds)} style={smallCheckStyle(alAllSelected)}>
                {alAllSelected && <Icon name="check" size={14} color="#fff" />}
              </div>
              <span style={{ fontSize: '12.5px', color: 'var(--dim)' }}>Tümünü seç</span>
              <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--dim)' }}>
                <span style={{ color: 'var(--text)', fontWeight: 600 }}>{alSelectedCount}</span> seçili ·{' '}
                {newVisibleIds.length + ' yeni'}
              </span>
              <button
                onClick={openAckSelected}
                disabled={acking || alSelectedCount === 0}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '7px',
                  padding: '9px 14px',
                  background: 'var(--accent)',
                  border: 'none',
                  borderRadius: '9px',
                  color: '#fff',
                  fontSize: '12.5px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                <Icon name="done_all" size={16} />
                {ackSelBtnLabel}
              </button>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {alerts.map((a) => {
              // SEV_META: [renk, arka plan, kenarlık, etiket]
              const sm = SEV_META[a.severity] || SEV_META.info;
              const isNew = a.status === 'new';
              const sel = !!selected[a.id];
              const isOpen = !!expanded[a.id];
              return (
                <div
                  key={a.id}
                  style={{
                    background: 'var(--surface)',
                    border: '1px solid var(--border)',
                    borderLeft: '3px solid ' + sm[0],
                    borderRadius: '10px',
                    overflow: 'hidden',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '13px 15px' }}>
                    {isNew && (
                      <div onClick={() => toggleAlSelect(a.id)} style={smallCheckStyle(sel)}>
                        {sel && <Icon name="check" size={14} color="#fff" />}
                      </div>
                    )}
                    <span
                      style={{
                        width: '34px',
                        height: '34px',
                        borderRadius: '9px',
                        background: sm[1],
                        border: '1px solid ' + sm[2],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: 'none',
                      }}
                    >
                      <Icon name={a.icon} size={19} color={sm[0]} />
                    </span>
                    <div onClick={() => toggle(a.id)} style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text)' }}>{a.title}</span>
                        <span
                          style={{
                            fontSize: '10px',
                            fontWeight: 700,
                            letterSpacing: '.04em',
                            padding: '2px 7px',
                            borderRadius: '5px',
                            color: sm[0],
                            background: sm[1],
                            border: '1px solid ' + sm[2],
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {sm[3]}
                        </span>
                      </div>
                      <div style={{ fontSize: '11.5px', color: 'var(--dim)', marginTop: '3px' }}>
                        {/* fmtRel KENDİ metin düğümünde kalmalı ("12 dk önce" çevirisi
                            düğüm başınadır) — bu yüzden ad birleştirilmez, ayıracıyla
                            birlikte koşullu basılır (bkz. i18n.ts translatePhrase). */}
                        {a.agentName ? a.agentName + ' · ' : ''}
                        {brandLabel(a.brand)} · {fmtRel(a.createdAt, nowA)}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--dim)', marginTop: '5px', textWrap: 'pretty' }}>
                        {a.description}
                      </div>
                    </div>
                    {/* Sağdaki damga uyarının AİT OLDUĞU gündür, oluşturulma anı DEĞİL.
                        Eskiden burada `createdAt` vardı: 28-07'ye ait bir boşluk 29-07'de
                        hesaplandığında satır "29.07 10:25" yazıyor, satırdaki tek diğer
                        zaman da "5 sa önce" olduğu için uyarı bugüne aitmiş gibi
                        okunuyordu. Oluşturulma anı ayrıntı panelinde duruyor. Gün
                        boyutu olmayan kuralda (bilinmeyen_ajan vb.) eski davranış. */}
                    <span style={{ fontSize: '11px', color: 'var(--faint)', whiteSpace: 'nowrap' }}>
                      {a.dayIst ? fmtDate(a.dayIst) : fmtStamp(a.createdAt, tzOfBrands([a.brand]))}
                    </span>
                    {isNew && (
                      <button
                        onClick={() => openAckOne(a.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '7px 12px',
                          background: 'var(--accent-soft)',
                          border: '1px solid var(--accent-border)',
                          borderRadius: '8px',
                          color: 'var(--accent-text2)',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Icon name="check" size={15} />
                        Onayla
                      </button>
                    )}
                    {!isNew && (
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '11.5px',
                          color: '#37c46b',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Icon name="task_alt" size={16} />
                        Onaylandı
                      </span>
                    )}
                    <button
                      onClick={() => toggle(a.id)}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', padding: '2px' }}
                    >
                      <Icon name={isOpen ? 'expand_more' : 'chevron_right'} size={20} color="var(--faint)" />
                    </button>
                  </div>
                  {isOpen && (
                    <div style={{ padding: '12px 15px 14px', borderTop: '1px solid var(--border)', background: 'var(--row)' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: '10px' }}>
                        {a.payload.map((p, i) => (
                          <div
                            key={i}
                            style={{
                              background: 'var(--inset)',
                              border: '1px solid var(--border)',
                              borderRadius: '8px',
                              padding: '8px 11px',
                            }}
                          >
                            <div style={{ fontSize: '9.5px', color: 'var(--faint)', letterSpacing: '.04em', marginBottom: '3px' }}>
                              {p[0]}
                            </div>
                            <div style={{ fontSize: '12.5px', color: 'var(--text)', fontWeight: 600 }}>{p[1]}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--faint)', marginTop: '11px' }}>
                        Oluşturuldu: {fmtStamp(a.createdAt, tzOfBrands([a.brand]))}
                      </div>
                      {a.ackAt && (
                        <div style={{ fontSize: '11px', color: 'var(--faint)', marginTop: '4px' }}>
                          {/* Onaylayan HESABIN adı (server ack_by = kullanıcının display_name'i,
                              §admin: vegas/prime süpervizör hesapları marka adını taşır). Eskiden
                              sabit "admin" basılırdı → kim onayladığı görünmezdi. ackBy boşsa
                              (ack_by kolonundan önceki eski satır) ad basılmaz — isim UYDURULMAZ. */}
                          Onaylandı: {fmtStamp(a.ackAt, tzOfBrands([a.brand]))}
                          {a.ackBy ? ' · ' + a.ackBy : ''}
                        </div>
                      )}
                      {/* Açıklama (migration 026). Boş = 026 öncesi onay; etiket basılmaz (§6). */}
                      {a.ackNote && (
                        <div
                          style={{
                            marginTop: '8px',
                            padding: '8px 11px',
                            background: 'var(--inset)',
                            border: '1px solid var(--border)',
                            borderRadius: '8px',
                            fontSize: '12px',
                            color: 'var(--text)',
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'anywhere',
                          }}
                        >
                          <div style={{ fontSize: '9.5px', color: 'var(--faint)', letterSpacing: '.04em', marginBottom: '3px' }}>
                            Onay açıklaması
                          </div>
                          {a.ackNote}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {ackPending && (
        <Modal onClose={closeAck} maxWidth={480}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
            <Icon name="rate_review" size={20} color="var(--accent)" />
            <div style={{ fontSize: '16px', fontWeight: 700 }}>Onay açıklaması</div>
          </div>
          <div style={{ fontSize: '12.5px', color: 'var(--dim)', marginBottom: '14px' }}>
            {/* Sayı taşıyan dizgi TEK ifade — i18n translatePhrase regex'i (CLAUDE.md §2). */}
            {ackPending.length === 1
              ? 'Bu uyarıyı neden onayladığınızı yazın. Açıklamasız onay kaydedilmez.'
              : ackPending.length + ' uyarı onaylanacak — aynı açıklama hepsine yazılır. Açıklamasız onay kaydedilmez.'}
          </div>
          <textarea
            autoFocus
            value={ackNote}
            onChange={(e) => setAckNote(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && canConfirmAck) void confirmAck();
            }}
            placeholder="Açıklama (zorunlu)"
            maxLength={ACK_NOTE_MAX}
            rows={4}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.45, marginBottom: '6px' }}
          />
          <div style={{ fontSize: '11px', color: 'var(--faint)', textAlign: 'right', marginBottom: '12px' }}>
            {ackNote.length + ' / ' + ACK_NOTE_MAX}
          </div>
          {ackErr && <div style={{ fontSize: '12px', color: '#ef6d6d', marginBottom: '10px' }}>{ackErr}</div>}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button onClick={closeAck} disabled={acking} style={ghostBtnStyle}>Vazgeç</button>
            <button
              onClick={() => void confirmAck()}
              disabled={!canConfirmAck}
              style={{ ...primaryBtnStyle, opacity: canConfirmAck ? 1 : 0.45 }}
            >
              {acking ? 'Onaylanıyor…' : 'Onayla'}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
